import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Heart, MapPin, Wrench, User, ArrowLeft, HardHat, Images } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GalleryPage({ onBack, isAuthenticated }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchGallery = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/gallery`); setItems(data); }
    catch (error) { console.error("Gallery fetch error:", error.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  const handleLike = async (galleryId) => {
    if (!isAuthenticated) { toast.error('Connectez-vous pour aimer un post'); return; }
    try {
      const { data } = await axios.post(`${API}/gallery/${galleryId}/like`, {}, { withCredentials: true });
      setItems(prev => prev.map(item =>
        item.id === galleryId ? { ...item, likes: data.likes, user_liked: data.liked } : item
      ));
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="gallery-page">
      <nav className="bg-[#09090B] border-b border-[#27272A] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#F4F4F5] transition-colors" data-testid="gallery-back-btn">
              <ArrowLeft size={18} />
            </button>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[#8B5CF6] flex items-center justify-center">
                <HardHat size={18} weight="duotone" className="text-white" />
              </div>
              <span className="font-bold text-lg tracking-tight text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Galerie</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="text-center mb-10 animate-fade-in-up">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2 text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Vitrine des artisans
          </h1>
          <p className="text-[#A1A1AA]">Les meilleurs projets BTP de Mayotte partagés par la communauté</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin w-6 h-6 border-2 border-[#3F3F46] border-t-[#8B5CF6] rounded-full" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 animate-fade-in" data-testid="gallery-empty">
            <Images size={48} weight="duotone" className="mx-auto text-[#3F3F46] mb-4" />
            <p className="text-[#A1A1AA] font-medium">La galerie est vide pour le moment</p>
            <p className="text-sm text-[#71717A] mt-1">Les artisans pourront bientôt y partager leurs meilleurs projets !</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((item, index) => (
              <div key={item.id} className="bg-[#18181B] border border-[#27272A] rounded-lg overflow-hidden animate-fade-in-up hover:border-[#8B5CF6]/30 transition-all"
                style={{ animationDelay: `${index * 0.05}s` }} data-testid={`gallery-item-${item.id}`}>
                {/* Header */}
                <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-[#8B5CF6]/15 flex items-center justify-center">
                      <User size={14} weight="duotone" className="text-[#A78BFA]" />
                    </div>
                    <span className="text-sm font-medium text-[#E4E4E7]">{item.user_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-[#71717A]">
                    <span className="flex items-center gap-1"><MapPin size={12} weight="duotone" />{item.city}</span>
                    <span className="flex items-center gap-1"><Wrench size={12} weight="duotone" />{item.construction_type}</span>
                  </div>
                </div>
                {/* Content */}
                <div className="p-4">
                  {item.title && <h3 className="font-bold text-base mb-2 text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{item.title}</h3>}
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#E4E4E7] line-clamp-6">{item.content}</p>
                </div>
                {/* Footer */}
                <div className="px-4 py-3 border-t border-[#27272A] flex items-center justify-between">
                  <button onClick={() => handleLike(item.id)} data-testid={`like-btn-${item.id}`}
                    className={`flex items-center gap-1.5 text-sm font-medium transition-all ${item.user_liked ? 'text-red-400' : 'text-[#A1A1AA] hover:text-red-400'}`}>
                    <Heart size={18} weight={item.user_liked ? "fill" : "duotone"} />
                    <span>{item.likes || 0}</span>
                  </button>
                  {item.mode === 'avant_apres' && (
                    <span className="px-2 py-0.5 bg-[#8B5CF6]/15 text-[#A78BFA] rounded text-[10px] font-bold">AVANT/APRÈS</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
